// CirclingAreaLayer.tsx — Phase C: paint the circle-to-land area on the map.
//
// SAFETY-CRITICAL. A misrendered polygon misleads a pilot into TERPS-prohibited
// airspace. Hard-coded guardrails (from data/circling-minima-phase-c-spec.md):
//   - No polygon when computeCirclingRadius() falls back (MDA unknown).
//   - No polygon when the plate has `na_total` restrictions.
//   - 'note'-type restrictions surface a ⚠ on the legend chip but DO NOT
//     modify the polygon (parser couldn't structure them → trust the plate).
//   - When polygon-clipping returns empty (full subtraction by sector
//     wedges), render only the legend chip.
//   - Legend chip is MANDATORY when the layer is rendering. Pilots scan
//     the map; they need the "advisory only — verify against plate" disclaimer.
//
// All pure-logic decisions live in src/lib/circlingArea.ts so they can
// be unit-tested without dragging in React + zustand stores (which would
// fail in vitest's 'node' env at module init time).
//
// Mount: alongside other side-effect layers in MapPage.tsx. Two React
// pieces export from this file:
//   - `<CirclingAreaLayer />`  — side-effect (returns null, pushes
//     MapLibre sources/layers).
//   - `<CirclingAreaLegend />` — HUD-style chip. The spec calls this
//     "mandatory" so it's owned by this feature, not MapTopBar.
//
// Both pieces subscribe to the same plan/aircraft/uiStore inputs and
// share computed state through a module-level listener pattern.
// Network responses are memoized in module-level caches keyed by
// `${icao}|${proc}` so the two subscribers don't double-fetch.

import { useEffect, useRef, useState } from 'react';
import { useLayerVisibilityStore } from '../stores/layerVisibilityStore';
import { useAircraftStore, selectActiveAircraft } from '../stores/aircraftStore';
import { usePlanStore } from '../stores/planStore';
import { fetchCirclingMinima, type CirclingMinimaApiResponse } from '../lib/plates';
import { fetchNasrAirport, type NasrAirportDetail } from '../lib/airportNasr';
import { type ApproachCategory } from '../lib/circlingRadius';
import { getEffectiveApproachCategory } from '../lib/aircraftClass';
import {
  findActiveIap,
  computeCirclingArea,
  EMPTY_CIRCLING_AREA_STATE,
  type CirclingAreaState,
} from '../lib/circlingArea';
import { getMapInstance } from './MapView';
import { registerHatchPatterns } from './hatchPatterns';
import type {
  Map as MlMap,
  GeoJSONSource,
  FillLayerSpecification,
  LineLayerSpecification,
} from 'maplibre-gl';

// Re-export the pure types/helpers used by the integration tests and by
// callers that prefer a single import surface.
export {
  computeCirclingArea,
  extractRunwayFromProcIdent,
  type CirclingAreaState,
} from '../lib/circlingArea';

// ─── MapLibre source / layer ids ────────────────────────────────────────────

const LEGAL_SOURCE_ID = 'circling-area-legal';
const LEGAL_FILL_LAYER_ID = 'circling-area-legal-fill';
const LEGAL_LINE_LAYER_ID = 'circling-area-legal-line';

const EXCLUDED_SOURCE_ID = 'circling-area-excluded';
const EXCLUDED_FILL_LAYER_ID = 'circling-area-excluded-fill';
const EXCLUDED_LINE_LAYER_ID = 'circling-area-excluded-line';

// Insert the circling-area fills BELOW the plan-route lines so the route
// always paints on top (pilot priority: see the route first; the
// circling area is supplementary).
const BEFORE_ID = 'plan-line-glow';

// Debounce plan / aircraft changes — wiring a transition can mutate the
// store several times in quick succession; we only need one recompute.
const RECOMPUTE_DEBOUNCE_MS = 300;

// ─── Module-level fetch caches (deduplicate concurrent fetches) ─────────────
//
// (Codex audit 2026-05-18 finding 6) Don't cache failed fetches. The
// previous code .catch'd into null and stored that, so any transient
// network blip permanently poisoned the entry for that (icao, proc).
// Now we cache the IN-FLIGHT promise; if it resolves to null OR rejects,
// we evict the entry so the next caller retries.

const minimaCache = new Map<string, Promise<CirclingMinimaApiResponse | null>>();
const nasrCache = new Map<string, Promise<NasrAirportDetail | null>>();

function cachedFetchMinima(icao: string, proc: string): Promise<CirclingMinimaApiResponse | null> {
  const key = `${icao.toUpperCase()}|${proc.toUpperCase()}`;
  let p = minimaCache.get(key);
  if (!p) {
    p = fetchCirclingMinima(icao, proc).then(
      (v) => {
        if (v === null) minimaCache.delete(key); // don't pin a transient null
        return v;
      },
      (e) => {
        minimaCache.delete(key);
        throw e;
      },
    ).catch(() => null);
    minimaCache.set(key, p);
  }
  return p;
}

function cachedFetchNasr(icao: string): Promise<NasrAirportDetail | null> {
  const key = icao.toUpperCase();
  let p = nasrCache.get(key);
  if (!p) {
    p = fetchNasrAirport(icao).then(
      (r) => {
        const air = r?.airport ?? null;
        if (air === null) nasrCache.delete(key);
        return air;
      },
      (e) => {
        nasrCache.delete(key);
        throw e;
      },
    ).catch(() => null);
    nasrCache.set(key, p);
  }
  return p;
}

// ─── Shared layer/legend state plumbing ─────────────────────────────────────

let currentState: CirclingAreaState = EMPTY_CIRCLING_AREA_STATE;
const listeners = new Set<(s: CirclingAreaState) => void>();
function setCurrentState(next: CirclingAreaState): void {
  currentState = next;
  for (const fn of listeners) fn(next);
}
function subscribeCurrentState(fn: (s: CirclingAreaState) => void): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

// ─── MapLibre layer scaffolding ─────────────────────────────────────────────

function emptyFC(): GeoJSON.FeatureCollection {
  return { type: 'FeatureCollection', features: [] };
}

function ensureMapLayers(map: MlMap): void {
  // Hatch patterns are used by future styling tweaks. Idempotent.
  registerHatchPatterns(map);

  if (!map.getSource(LEGAL_SOURCE_ID)) {
    map.addSource(LEGAL_SOURCE_ID, { type: 'geojson', data: emptyFC() });
  }
  if (!map.getSource(EXCLUDED_SOURCE_ID)) {
    map.addSource(EXCLUDED_SOURCE_ID, { type: 'geojson', data: emptyFC() });
  }
  const beforeId = map.getLayer(BEFORE_ID) ? BEFORE_ID : undefined;
  if (!map.getLayer(LEGAL_FILL_LAYER_ID)) {
    const spec: FillLayerSpecification = {
      id: LEGAL_FILL_LAYER_ID,
      type: 'fill',
      source: LEGAL_SOURCE_ID,
      paint: {
        'fill-color': '#22c55e', // green-500
        'fill-opacity': 0.2,
      },
    };
    map.addLayer(spec, beforeId);
  }
  if (!map.getLayer(LEGAL_LINE_LAYER_ID)) {
    const spec: LineLayerSpecification = {
      id: LEGAL_LINE_LAYER_ID,
      type: 'line',
      source: LEGAL_SOURCE_ID,
      paint: {
        // Bright lime stroke for high contrast against terrain + chart bg.
        // Boss feedback 2026-05-17: original green-700/1px/60% was too
        // muted to spot at a glance.
        'line-color': '#a3e635', // lime-400
        'line-width': 3,
        'line-opacity': 0.95,
      },
    };
    map.addLayer(spec, beforeId);
  }
  if (!map.getLayer(EXCLUDED_FILL_LAYER_ID)) {
    const spec: FillLayerSpecification = {
      id: EXCLUDED_FILL_LAYER_ID,
      type: 'fill',
      source: EXCLUDED_SOURCE_ID,
      paint: {
        // Magenta translucent fill — distinct from red SUA / TFR hatching.
        'fill-color': '#d946ef', // fuchsia-500
        'fill-opacity': 0.35,
      },
    };
    map.addLayer(spec, beforeId);
  }
  if (!map.getLayer(EXCLUDED_LINE_LAYER_ID)) {
    const spec: LineLayerSpecification = {
      id: EXCLUDED_LINE_LAYER_ID,
      type: 'line',
      source: EXCLUDED_SOURCE_ID,
      paint: {
        'line-color': '#d946ef', // fuchsia-500
        'line-width': 2,
        'line-opacity': 0.95,
        'line-dasharray': [3, 2],
      },
    };
    map.addLayer(spec, beforeId);
  }
}

function removeMapLayers(map: MlMap): void {
  if (map.getLayer(EXCLUDED_LINE_LAYER_ID)) map.removeLayer(EXCLUDED_LINE_LAYER_ID);
  if (map.getLayer(EXCLUDED_FILL_LAYER_ID)) map.removeLayer(EXCLUDED_FILL_LAYER_ID);
  if (map.getLayer(LEGAL_LINE_LAYER_ID)) map.removeLayer(LEGAL_LINE_LAYER_ID);
  if (map.getLayer(LEGAL_FILL_LAYER_ID)) map.removeLayer(LEGAL_FILL_LAYER_ID);
  if (map.getSource(EXCLUDED_SOURCE_ID)) map.removeSource(EXCLUDED_SOURCE_ID);
  if (map.getSource(LEGAL_SOURCE_ID)) map.removeSource(LEGAL_SOURCE_ID);
}

function clearMapSources(map: MlMap): void {
  const legal = map.getSource(LEGAL_SOURCE_ID) as GeoJSONSource | undefined;
  if (legal) legal.setData(emptyFC());
  const excluded = map.getSource(EXCLUDED_SOURCE_ID) as GeoJSONSource | undefined;
  if (excluded) excluded.setData(emptyFC());
}

// ─── Side-effect layer component ────────────────────────────────────────────

export function CirclingAreaLayer() {
  const visible = useLayerVisibilityStore((s) => s.layers.circlingArea);
  const route = usePlanStore((s) => s.route);
  const aircraft = useAircraftStore(selectActiveAircraft);

  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inFlightAc = useRef<AbortController | null>(null);

  // Bumped whenever the map style finishes (re-)loading. Used as a
  // dependency of the recompute effect so a basemap swap (which nukes
  // our sources/layers) triggers a fresh fetch + paint. Also lifts the
  // initial-mount race where the compute resolves before the style is
  // loaded — the sources don't exist yet, so setData would no-op.
  // (2026-05-18 boss report: "circling area doesn't show up unless I
  // toggle the layer off and back on.")
  const [styleTick, setStyleTick] = useState(0);

  // ── Mount/unmount layer scaffolding ─────────────────────────────────
  useEffect(() => {
    const map = getMapInstance();
    if (!map) return;
    if (!visible) {
      removeMapLayers(map);
      setCurrentState(EMPTY_CIRCLING_AREA_STATE);
      return;
    }
    if (map.isStyleLoaded()) {
      ensureMapLayers(map);
    } else {
      const handler = () => {
        ensureMapLayers(map);
        map.off('load', handler);
      };
      map.on('load', handler);
    }
    return () => {
      if (!visible) {
        removeMapLayers(map);
      }
    };
  }, [visible]);

  // Re-ensure layers + force recompute on map style swap.
  // MapLibre fires 'style.load' both on initial mount and every basemap
  // swap. Sources/layers added before the style swap get dropped, so
  // we need to re-ensure them and recompute the polygon. Bumping
  // styleTick triggers the compute effect below.
  useEffect(() => {
    const map = getMapInstance();
    if (!map) return;
    const handler = (): void => {
      if (visible) ensureMapLayers(map);
      setStyleTick((n) => n + 1);
    };
    map.on('style.load', handler);
    return () => {
      map.off('style.load', handler);
    };
  }, [visible]);

  // ── Cleanup on unmount ──────────────────────────────────────────────
  useEffect(() => {
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      inFlightAc.current?.abort();
      const map = getMapInstance();
      if (map) removeMapLayers(map);
      setCurrentState(EMPTY_CIRCLING_AREA_STATE);
    };
  }, []);

  // ── Recompute on plan / aircraft / visibility change ────────────────
  useEffect(() => {
    if (!visible) {
      setCurrentState(EMPTY_CIRCLING_AREA_STATE);
      return;
    }
    const iap = findActiveIap(route);
    if (!iap) {
      setCurrentState(EMPTY_CIRCLING_AREA_STATE);
      const map = getMapInstance();
      if (map) clearMapSources(map);
      return;
    }

    const category: ApproachCategory = aircraft
      ? getEffectiveApproachCategory(aircraft.icaoType)
      : 'A';

    // Debounce so a burst of plan mutations (IAP load → splice route →
    // update etd → ...) only triggers one fetch+compute.
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(async () => {
      inFlightAc.current?.abort();
      const ac = new AbortController();
      inFlightAc.current = ac;

      try {
        const [minima, airport] = await Promise.all([
          cachedFetchMinima(iap.airportIcao, iap.procIdent),
          cachedFetchNasr(iap.airportIcao),
        ]);
        if (ac.signal.aborted) return;
        if (!airport) {
          // NASR has no data for this airport (non-US, or unloaded).
          // Surface "data-missing" without attempting geometry.
          setCurrentState({
            ...EMPTY_CIRCLING_AREA_STATE,
            active: true,
            polygonRendered: false,
            category,
            airportIdent: iap.airportIcao,
            procIdent: iap.procIdent,
            status: 'data-missing',
          });
          const map = getMapInstance();
          if (map) clearMapSources(map);
          return;
        }

        const result = computeCirclingArea({
          airportIcao: iap.airportIcao,
          procIdent: iap.procIdent,
          arp: iap.arp,
          airport,
          minima,
          category,
        });

        setCurrentState(result.state);

        const map = getMapInstance();
        if (!map) return;

        // Defensive: on a cold session the style may still be loading
        // when this compute resolves. Calling ensureMapLayers idempotently
        // creates the sources if absent; if the style isn't ready yet,
        // wait for 'load' and apply the data once it is. This fixes the
        // "circling polygon doesn't render until I toggle the layer off
        // and back on" bug (2026-05-18 boss report) where the data was
        // computed before the sources existed.
        const applyData = (m: MlMap): void => {
          ensureMapLayers(m);
          const legalSrc = m.getSource(LEGAL_SOURCE_ID) as GeoJSONSource | undefined;
          const excludedSrc = m.getSource(EXCLUDED_SOURCE_ID) as GeoJSONSource | undefined;
          if (legalSrc) legalSrc.setData(result.legalFc);
          if (excludedSrc) excludedSrc.setData(result.excludedFc);
        };

        if (map.isStyleLoaded()) {
          applyData(map);
        } else {
          const handler = (): void => {
            if (ac.signal.aborted) return;
            applyData(map);
            map.off('load', handler);
          };
          map.on('load', handler);
        }
      } catch (err: unknown) {
        if ((err as { name?: string }).name === 'AbortError') return;
        // eslint-disable-next-line no-console
        console.warn('[CirclingAreaLayer] compute failed', err);
        setCurrentState(EMPTY_CIRCLING_AREA_STATE);
      }
    }, RECOMPUTE_DEBOUNCE_MS);

    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, [visible, route, aircraft, styleTick]);

  return null;
}

// ─── Legend chip (HUD-style) ────────────────────────────────────────────────

/**
 * Compact HUD chip rendered above the right-rail FAB cluster on MapPage.
 * Subscribes to the module-level state written by the layer.
 *
 * The chip is MANDATORY when `state.active === true` — the pilot must
 * see the "advisory only — verify against plate" disclaimer, even on
 * the na-total / circling-only / data-missing branches where no polygon
 * renders.
 */
export function CirclingAreaLegend() {
  const visible = useLayerVisibilityStore((s) => s.layers.circlingArea);
  const [state, setState] = useState<CirclingAreaState>(currentState);

  useEffect(() => {
    const unsub = subscribeCurrentState(setState);
    // Re-sync once on mount in case state changed between module init and
    // component mount.
    setState(currentState);
    return unsub;
  }, []);

  if (!visible) return null;
  if (!state.active) return null;

  // ─── na-total: red banner, no polygon ─────────────────────────────────
  if (state.status === 'na-total') {
    return (
      <ChipFrame variant="na">
        <div className="flex items-center gap-2">
          <span className="text-red-300 text-base leading-none">⛔</span>
          <div className="leading-tight">
            <div className="text-red-200 font-semibold tracking-wide">Circling NA</div>
            <div className="text-[10px] text-red-300/80">no circling authorized</div>
          </div>
        </div>
      </ChipFrame>
    );
  }

  // ─── circling-only: blue informational ────────────────────────────────
  if (state.status === 'circling-only') {
    return (
      <ChipFrame variant="info">
        <div className="flex items-center gap-2">
          <span className="text-sky-300 text-base leading-none">ℹ️</span>
          <div className="leading-tight">
            <div className="text-cockpit-100 font-medium">Circling-only approach</div>
            <div className="text-[10px] text-cockpit-400">no centerline polygon</div>
          </div>
        </div>
      </ChipFrame>
    );
  }

  // ─── data-missing: amber warning, no polygon ──────────────────────────
  if (state.status === 'data-missing') {
    return (
      <ChipFrame variant="warn">
        <div className="flex items-center gap-2">
          <span className="text-amber-300 text-base leading-none">⚠</span>
          <div className="leading-tight">
            <div className="text-amber-200 font-medium">
              Circling area · CAT {state.category ?? '—'}
            </div>
            <div className="text-[10px] text-amber-300/80">
              data unavailable — verify against plate
            </div>
          </div>
        </div>
      </ChipFrame>
    );
  }

  // ─── ok / note-advisory: green chip, polygon rendered ─────────────────
  const radius = state.radius;
  const advisoryLine = state.hasUnstructuredNotes
    ? '⚠ Restrictions apply — see plate'
    : '⚠ Advisory only — verify against plate';

  return (
    <ChipFrame variant="ok">
      <div className="flex items-center gap-2">
        <span className="text-green-300 text-base leading-none">🟢</span>
        <div className="leading-tight">
          <div className="text-cockpit-100 font-medium">
            Circling area · CAT {state.category ?? 'A'}
            {radius != null && (
              <span className="font-mono text-cockpit-200 ml-1">
                · {radius.radiusNm.toFixed(1)} NM
              </span>
            )}
          </div>
          {radius != null && (
            <div className="text-[10px] text-cockpit-300/90">({radius.bandLabel} band)</div>
          )}
          <div className="text-[10px] text-amber-300/80 mt-0.5">{advisoryLine}</div>
        </div>
      </div>
    </ChipFrame>
  );
}

interface ChipFrameProps {
  variant: 'ok' | 'warn' | 'na' | 'info';
  children: React.ReactNode;
}

function ChipFrame({ variant, children }: ChipFrameProps) {
  const ringColor =
    variant === 'na'   ? 'border-red-500/60' :
    variant === 'warn' ? 'border-amber-500/60' :
    variant === 'info' ? 'border-sky-500/60' :
                         'border-green-600/50';
  return (
    <div
      className={[
        'pointer-events-auto',
        'bg-cockpit-800/95 backdrop-blur',
        'border rounded-lg px-3 py-2 text-xs shadow-bar',
        'max-w-[18rem]',
        ringColor,
      ].join(' ')}
    >
      {children}
    </div>
  );
}

// ─── Test-only state-plumbing hooks ─────────────────────────────────────────

export const __test = {
  setCurrentState,
  subscribeCurrentState,
  getCurrentState: () => currentState,
  EMPTY_STATE: EMPTY_CIRCLING_AREA_STATE,
  // caches — tests can clear between cases
  minimaCache,
  nasrCache,
};
